import React, { useEffect, useState } from "react";
import { OAUTH_REDIRECT_URL } from "@/api/base";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  signInWithProvider,
  SocialSigninResponse,
  signOutUser,
  isRetryableOAuthError,
} from "@/api/login";
import { useNavigate } from "react-router-dom";
import { Spinner, useToast } from "@chakra-ui/react";
import { LoadingPage } from "./LoadingPage";
import { submitStoredWebSignup } from "@/lib/webSignup";

// The OAuth code is single-use. runOnce only guards one mount; these persist
// across reloads (per-tab) so a code that was actually spent isn't exchanged
// again. Wrapped in try/catch since storage can throw (private mode / quota).
const CONSUMED_CODE_KEY = "oauthConsumedCode";
function wasCodeConsumed(code: string): boolean {
  try {
    return sessionStorage.getItem(CONSUMED_CODE_KEY) === code;
  } catch {
    return false;
  }
}
function markCodeConsumed(code: string): void {
  try {
    sessionStorage.setItem(CONSUMED_CODE_KEY, code);
  } catch {
    // storage unavailable — degrade gracefully (runOnce still guards this mount)
  }
}

const RedirectPage: React.FC = () => {
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { search } = useLocation();
  const nav = useNavigate();
  const toast = useToast();

  const searchParams = React.useMemo(
    () => new URLSearchParams(search),
    [search]
  );
  const runOnce = React.useRef(false);

  useEffect(() => {
    const code = searchParams.get("code");
    const state = searchParams.get("state");
    const isNativeIOS = searchParams.get("native") == "true";
    if (!code || !state) {
      setError("No code or state provided");
      setLoading(false);
      return;
    }
    if (runOnce.current) {
      return;
    }

    runOnce.current = true;
    const redirectUrl = OAUTH_REDIRECT_URL + "/redirect";
    const provider = JSON.parse(localStorage.getItem("provider") || "{}");

    if (provider.state !== state) {
      setError("State mismatch error");
      setLoading(false);
      return;
    }

    // Block only a code we already spent on a prior load (marked below on a
    // definitive outcome) — not one that merely hit a transient failure.
    if (wasCodeConsumed(code)) {
      setError("Failed to exchange code. Please try again.");
      setLoading(false);
      return;
    }

    const isApple = provider.name === "apple" || provider.name === "apple-web";
    const skipName = isApple;
    signInWithProvider(
      provider.name,
      code,
      provider.codeVerifier,
      redirectUrl,
      skipName,
      isNativeIOS
    )
      .then(async (signinResponse: SocialSigninResponse) => {
        // Exchange succeeded — the code is spent; don't let a reload re-send it.
        markCodeConsumed(code);
        if (cameFromSignup()) {
          try {
            await handleWebSignup();
          } catch (error) {
            console.error("Failed to handle web signup:", error);
            setError("Failed to handle web signup");
            setLoading(false);
            return;
          }
          if (provider.name === "apple" || provider.name === "apple-web") {
            localStorage.removeItem("provider");
            localStorage.removeItem("authMode");
            nav(`/name`);
          } else {
            localStorage.removeItem("provider");
            localStorage.removeItem("authMode");
            nav("/placement");
          }
          return;
        } else {
          const authData = signinResponse.authData;
          const createdAt = authData.record.created;
          const withinSeconds = 7;
          if (wasJustCreated(createdAt, withinSeconds)) {
            toast({
              title: "Account not found",
              description:
                "No account found with this email. Please sign up first.",
              status: "error",
              duration: 5000,
              isClosable: true,
              position: "top",
            });
            signOutUser();
            localStorage.removeItem("provider");
            localStorage.removeItem("authMode");
            nav("/login");
            return;
          } else {
            nav("/home/learn");
          }
        }
      })
      .catch((err) => {
        // Only a definitive rejection (4xx) means the code was actually spent;
        // leave transient failures unmarked so a reload can legitimately retry.
        if (!isRetryableOAuthError(err)) {
          markCodeConsumed(code);
        }
        console.error("Failed to exchange code:", err);
        setError("Failed to exchange code. Please try again.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [search, nav, searchParams, toast]);

  const cameFromSignup = () => {
    return localStorage.getItem("authMode") === "signup";
  };
  const handleWebSignup = async () => {
    await submitStoredWebSignup();
  };

  return (
    <div className="flex flex-col justify-center items-center h-screen">
      {isLoading ? (
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor="gray.200"
          color="blue.500"
          size="xl"
        />
      ) : error ? (
        <div>
          <p className="text-center max-w-md">
            Something went wrong. Please try again and contact
            support@parrotapp.com if the issue persists.
          </p>
          <Button
            onClick={() => {
              // correctly send the user back the the retry location
              if (cameFromSignup()) {
                nav("/signup");
              } else {
                nav("/login");
              }
            }}
            className="mt-4"
          >
            Try again
          </Button>
        </div>
      ) : (
        <LoadingPage />
      )}
    </div>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export function wasJustCreated(timestamp: string, seconds: number): boolean {
  const currentTime = new Date().getTime();
  const inputTime = new Date(timestamp).getTime();
  const differenceInSeconds = (currentTime - inputTime) / 1000;
  return differenceInSeconds <= seconds;
}

export default RedirectPage;
